import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ButtonGroup,
  Snackbar,
  Alert,
  Checkbox,
} from "@mui/material";
import apiService from "../components/ReasuableFuncations/api";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { Chart } from "chart.js";
import ReportFilter from "../components/filter/ReportFilter";

const ReportingModule = ({ project }) => {
  const [userData, setUserData] = useState(null);
  const [organization, setOrganization] = useState("");
  const [isOrgDisabled, setIsOrgDisabled] = useState(false);
  const [budgets, setBudgets] = useState([]);
  const [ictStatistics, setIctStatistics] = useState([]);
  const [data, setdata] = useState([])
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [fundingType, setFundingType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filters, setFilters] = useState({});
  const [reportData, setReportData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar open state
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message to display in Snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("error"); // Error severity (e.g., "error" or "success")
  const [selectedProjects, setSelectedProjects] = useState([]);

  const userDataFromStorage = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (!userDataFromStorage) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    setUserData(userDataFromStorage);

    if (project) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        project: project.projectName,
      }));
    }

    if (userDataFromStorage) {
      switch (userDataFromStorage.selectedOption) {
        case "Comesa":
          setOrganization("COMESA");
          break;
        case "Regional Network":
          setOrganization(userDataFromStorage.regionalNetworkOption);
          setIsOrgDisabled(true);
          break;
        case "Member State":
          setOrganization(userDataFromStorage.memberStateOption);
          setIsOrgDisabled(true);
          break;
        default:
          break;
      }
    }
  }, [project]);

  const fetchBudgets = async () => {
    // setLoading(true);
    if (!userDataFromStorage) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    const {
      selectedOption,
      regionalNetworkOption,
      memberStateOption,
      countryOption,
    } = userDataFromStorage;
    let url = "/api/budgets";

    try {
      let response;
      if (selectedOption === "Comesa") {
        response = await apiService.get(`${url}`);
      } else if (
        selectedOption === "Regional Network" &&
        regionalNetworkOption
      ) {
        response = await apiService.get(
          `${url}?regionalNetwork=${regionalNetworkOption}`
        );
      } else if (selectedOption === "Member State" && memberStateOption) {
        response = await apiService.get(`${url}?country=${countryOption}`);
      }

      if (response.statusText !== "OK") {
        throw new Error("Failed to fetch budgets");
      }

      const fetchedBudgets = response.data;
      const filteredBudgets = fetchedBudgets.filter(
        (item) => item && item.project !== null
      );
      setBudgets(filteredBudgets);
    } catch (error) {
      console.error("Error fetching budgets:", error);
      setSnackbarMessage("Failed to fetch budgets. Please try again later.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchDataByType = async (type, setter) => {
    if (!userDataFromStorage) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    
    const {
      selectedOption,
      regionalNetworkOption,
      memberStateOption,
      countryOption,
    } = userDataFromStorage;
    
    const url = type === 'ict' ? '/api/ict-statistics' : '/api/data';
  
    try {
      let response;
      if (selectedOption === "Comesa") {
        response = await apiService.get(url);
      } else if (selectedOption === "Regional Network" && regionalNetworkOption) {
        response = await apiService.get(`${url}?regionalNetwork=${regionalNetworkOption}`);
      } else if (selectedOption === "Member State" && memberStateOption) {
        response = await apiService.get(`${url}?country=${countryOption}`);
      }
  
      // Check if response exists and has data
      if (!response || !response.data) {
        throw new Error(`No ${type} data received from server`);
      }
  
      const fetchedData = response.data;
      const filteredData = Array.isArray(fetchedData) 
        ? fetchedData.filter(item => item && item.project !== null)
        : [];
      
      setter(filteredData);
  
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
      // Don't show error for initial data load
      if (type !== 'data') {
        setSnackbarMessage(`Failed to fetch ${type} data. Please try again later.`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };
  
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchBudgets();
      await fetchDataByType('ict', setIctStatistics);
      await fetchDataByType('data', setdata);
    };
  
    loadInitialData();
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const generateReport = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) {
        console.error("User data not found. Redirecting to login.");
        return;
      }

      const { selectedOption, regionalNetworkOption, memberStateOption } =
        userData;

      const params = {
        ...filters,
        startDate,
        endDate,
        status,
        fundingType,
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption: country,
        organization,
      };

      const response = await apiService.post(`/report`, params);
      if (response.statusText !== "OK")
        throw new Error("failed to get the data");
      const projectData = response.data;

      // Add budget data when available, keep all projects regardless of budget status
      const enrichedProjectData = projectData.map((project) => {
        const matchingBudget = budgets.find(
          (budget) => budget.project?._id === project._id
        );
        return {
          ...project,
          expectedBudget: matchingBudget?.expectedBudget || 0,
          actualBudget: matchingBudget?.actualBudget || 0,
        };
      });

      setReportData(enrichedProjectData);
      setCountry("");
      setFundingType("");
      setStartDate("");
      setEndDate("");
      setStatus("");
    } catch (error) {
      setSnackbarMessage("Failed to fetch report data. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleClearFilters = () => {
    setCountry("");
    setFundingType("");
    setStartDate("");
    setEndDate("");
    setStatus("");
    setReportData([]);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedProjects(reportData.map(project => project._id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleSelectProject = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const exportReport = (format) => {
    const selectedData = reportData.filter(project => 
      selectedProjects.includes(project._id)
    );

    if (selectedData.length === 0) {
      setSnackbarMessage("Please select at least one project to export");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    if (format === "PDF") {
      const doc = new jsPDF("portrait", "pt", "a4");
      const pageWidth = 595.28;
      const pageHeight = 841.89;
      const margins = {
        top: 10,
        bottom: 10,
        left: 15,
        right: 15,
        contentLeft: 30,   // Content padding inside the border
        contentRight: 30,  // Content padding inside the border
        contentTop: 20,    // Content padding inside the border
        contentBottom: 20  // Content padding inside the border
      };
      const lineHeight = 15;
      const maxWidth = pageWidth - margins.left - margins.right - margins.contentLeft - margins.contentRight;
  
      const drawPageBorder = () => {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(1);
        doc.rect(
          margins.left, 
          margins.top, 
          pageWidth - (margins.left + margins.right), 
          pageHeight - (margins.top + margins.bottom)
        );
      };
  
      const checkNewPage = (currentY, requiredSpace = 100) => {
        if (currentY + requiredSpace > pageHeight - margins.bottom - margins.contentBottom) {
          doc.addPage();
          drawPageBorder();
          return margins.top + margins.contentTop;
        }
        return currentY;
      };
  
      const addSectionHeader = (text, yPosition) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        yPosition = checkNewPage(yPosition, 30);
        doc.text(text, margins.left + margins.contentLeft, yPosition);
        doc.setLineWidth(0.5);
        doc.line(
          margins.left + margins.contentLeft, 
          yPosition + 5, 
          margins.left + margins.contentLeft + 200, 
          yPosition + 5
        );
        doc.setFont("helvetica", "normal");
        return yPosition + 25;
      };
  
      const addMultiLineText = (text, xPosition, yPosition, options = {}) => {
        const { 
          maxWidth: maxTextWidth = pageWidth - margins.left - margins.right - margins.contentLeft - margins.contentRight, 
          fontSize = 11 
        } = options;
        
        doc.setFontSize(fontSize);
        const splitText = doc.splitTextToSize(text, maxTextWidth);
        
        let currentY = yPosition;
        splitText.forEach(line => {
          currentY = checkNewPage(currentY, lineHeight);
          doc.text(line, xPosition, currentY);
          currentY += lineHeight;
        });
        
        return currentY;
      };
  
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Report Generated: ${new Date().toLocaleString()}`, 
        margins.left + margins.contentLeft, 
        margins.top - 20
      );
  
      selectedData.forEach((project, index) => {
        let currentY = margins.top + margins.contentTop;
  
        if (index > 0) {
          doc.addPage();
        }
  
        drawPageBorder();
  
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        currentY = checkNewPage(currentY, 30);
        doc.text(
          `Project Report: ${project.projectName}`, 
          margins.left + margins.contentLeft, 
          currentY
        );
        currentY += 30;
  
        const sections = [
          {
            title: "1. Project Overview",
            fields: [
              { key: "Project Name", value: project.projectName },
              { key: "Status", value: project.status },
              { key: "Start Date", value: project.startDate ? new Date(project.startDate).toLocaleDateString() : 'N/A' },
              { key: "End Date", value: project.endDate ? new Date(project.endDate).toLocaleDateString() : 'N/A' },
              { key: "Organization", value: project.organization },
              { key: "Expected Budget", value: project.expectedBudget ? `$${project.expectedBudget.toLocaleString()}` : 'N/A' },
              { key: "Actual Budget", value: project.actualBudget ? `$${project.actualBudget.toLocaleString()}` : 'N/A' },
              { key: "Funding Type", value: project.funding || 'N/A' }
            ]},
            {
              title: "2. Objectives",
              fields: [
                { key: "Overall Objective", value: project.description },
                ...(project.objectives ? 
                  project.objectives.split(',').map((obj, idx) => ({
                    key: `Specific Objective ${idx + 1}`, 
                    value: obj.trim()
                  })) : 
                  [{ key: "Specific Objectives", value: 'N/A' }])
              ]
            },
            {
              title: "3. Key Activities and Milestones",
              fields: [
                { key: "Major Activities", value: project.activityname },
                { key: "Key Milestone", value: project.milestone },
                { key: "Milestone Date", value: project.targetDate ? new Date(project.targetDate).toLocaleDateString() : 'N/A' }
              ]
            },
            {
              title: "4. Stakeholders",
              fields: [
                { key: "Key Partners", value: project.organization }
              ]
            },
            {
              title: "5. Challenges and Lessons Learned",
              fields: [
                { key: "Challenges", value: project.challenges },
                { key: "Lessons Learned", value: project.lessons }
              ]
            },
            {
              title: "6. Indicators and Achievements",
              fields: [
                { key: "Baseline", value: project.baseline },
                { key: "Target", value: project.target },
                { key: "Current Achievement", value: project.current },
                { key: "Achievement Percentage", value: `${project.percentageAchievement || 0}%` }
              ]
            },
            {
              title: "7. Collaboration Information",
              fields: [
                { key: "Collaborating Regions", value: project.collaboratingRegions ? project.collaboratingRegions.join(', ') : 'N/A' },
                { key: "Collaborating States", value: project.collaboratingStates ? project.collaboratingStates.map(state => state.name).join(', ') : 'N/A' }
              ]
            }
          
        ];
  
        sections.forEach(section => {
          currentY = checkNewPage(currentY, 100);
          
          currentY = addSectionHeader(section.title, currentY);
          
          section.fields.forEach(field => {
            currentY = checkNewPage(currentY);
            
            const fieldText = `• ${field.key}: ${field.value || 'N/A'}`;
            currentY = addMultiLineText(
              fieldText, 
              margins.left + margins.contentLeft, 
              currentY
            );
            
            currentY += 5;
          });
          
          currentY += 10;
        });
      });
  
      doc.save("project_detailed_report.pdf");
    } 
    else if (format === "Excel") {
      const excelData = selectedData.map((row, index) => ({
        "S.No": index + 1,
        "Project Name": row.projectName || 'N/A',
        "Status": row.status || 'N/A',
        "Start Date": row.startDate ? new Date(row.startDate).toLocaleDateString() : 'N/A',
        "End Date": row.endDate ? new Date(row.endDate).toLocaleDateString() : 'N/A',
        "Organization": row.organization || 'N/A',
        "Expected Budget": row.expectedBudget || 'N/A',
        "Actual Budget": row.actualBudget || 'N/A',
        "Funding Type": row.funding || 'N/A',
        "Collaborating Regions": row.collaboratingRegions ? row.collaboratingRegions.join(', ') : 'N/A',
        "Collaborating States": row.collaboratingStates ? row.collaboratingStates.map(state => state.name).join(', ') : 'N/A'
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      XLSX.utils.sheet_add_aoa(
        worksheet,
        [
          [`Report Generated: ${new Date().toLocaleString()}`],
          [`Organization: ${organization}`],
          [`Date Range: ${startDate} - ${endDate}`],
          [`Status: ${status || 'All'}`],
          [], 
        ],
        { origin: -1 }
      );

      const colWidths = [];
      Object.keys(excelData[0] || {}).forEach(() => {
        colWidths.push({ wch: 25 });
      });
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Projects Report");

      XLSX.writeFile(workbook, `project_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    }
};

  const renderTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox
                checked={selectedProjects.length === reportData.length}
                indeterminate={selectedProjects.length > 0 && selectedProjects.length < reportData.length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Start Date</TableCell>
            <TableCell>End Date</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Expected Budget</TableCell>
            <TableCell>Actual Budget</TableCell>
            <TableCell>Funding Type</TableCell>
            <TableCell>Collaborating Regions</TableCell>
            <TableCell>Collaborating States</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reportData.length < 0 ? (
            <Typography variant="h6">
              No data matching selected filter{" "}
            </Typography>
          ) : (
            <>
              {reportData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Checkbox
                      checked={selectedProjects.includes(row._id)}
                      onChange={() => handleSelectProject(row._id)}
                    />
                  </TableCell>
                  <TableCell>{row.projectName}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    {new Date(row.startDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(row.endDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{row.organization}</TableCell>
                  <TableCell>{row.expectedBudget}</TableCell>
                  <TableCell>{row.actualBudget}</TableCell>
                  <TableCell>{row.funding}</TableCell>
                  <TableCell>{Array.isArray(row.collaboratingRegions) ? row.collaboratingRegions.join(', ') : ''}</TableCell>
                  <TableCell>{Array.isArray(row.collaboratingStates) ? row.collaboratingStates.map(state => state.name).join(', ') : ''}</TableCell>
                </TableRow>
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg" sx={{ paddingRight: "32px" }}>
      <Paper elevation={20} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Report Generation
        </Typography>

        {/* <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Organization</InputLabel>
              <Select
                name="organization"
                value={organization}
                onChange={(event) => setOrganization(event.target.value)}
                label="Organization"
                disabled={isOrgDisabled}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="COMESA">COMESA</MenuItem>
                <MenuItem value="IGAD">IGAD</MenuItem>
                <MenuItem value="SADC">SADC</MenuItem>
                <MenuItem value="EAC">EAC</MenuItem>
                <MenuItem value="IOC">IOC</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              name="startDate"
              label="Start Date"
              type="date"
              required
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              name="endDate"
              label="End Date"
              type="date"
              required
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Ongoing">Ongoing</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Complete">Complete</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid> */}
        <ReportFilter
          organization={organization}
          setOrganization={setOrganization}
          isOrgDisabled={isOrgDisabled}
          setCountry={setCountry}
          country={country}
          setFundingType={setFundingType}
          fundingType={fundingType}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          status={status}
          setStatus={setStatus}
          handleClearFilters={handleClearFilters}
          handleGenerateReport={generateReport}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={generateReport}
          sx={{ mb: 2 }}
        >
          Generate Report
        </Button>

        {reportData.length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>{renderTable()}</Paper>
        )}

        <Grid container spacing={2}>
          <Grid item>
            <ButtonGroup variant="contained" aria-label="Export button group">
              <Button color="inherit" onClick={() => exportReport("PDF")}>
                Export as PDF
              </Button>
              <Button color="inherit" onClick={() => exportReport("Excel")}>
                Export as Excel
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReportingModule;